import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EMPTY, fromEvent, interval, merge } from 'rxjs';
import { catchError, exhaustMap, filter } from 'rxjs/operators';
import { LoginService } from './login.service';

export const KEEP_ALIVE_TICK_MS = 60_000;
export const RENEW_WHEN_LEFT_MS = 5 * 60_000;
export const WORKING_WITHIN_MS = 5 * 60_000;

/**
 * A form that issues no request lets the token expire under the user: a script is built for half
 * an hour, and the save that follows is answered with a session that is already over. This renews
 * the token while someone is working on the page, and leaves an abandoned tab to lapse.
 */
@Injectable({ providedIn: 'root' })
export class SessionKeepAliveService {
  private readonly loginService = inject(LoginService);
  private readonly destroyRef = inject(DestroyRef);

  private lastActivity = Date.now();

  start(): void {
    merge(
      fromEvent(document, 'keydown'),
      fromEvent(document, 'pointerdown'),
      fromEvent(document, 'visibilitychange')
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => (this.lastActivity = Date.now()));

    interval(KEEP_ALIVE_TICK_MS)
      .pipe(
        filter(() => this.isWorking()),
        exhaustMap(() => this.loginService.refreshToken().pipe(catchError(() => EMPTY))),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private isWorking(): boolean {
    const validUntil = this.loginService.tokenValidUntil;
    if (!validUntil || document.visibilityState !== 'visible') {
      return false;
    }

    const now = Date.now();

    return validUntil - now < RENEW_WHEN_LEFT_MS && now - this.lastActivity < WORKING_WITHIN_MS;
  }
}
